import { useState, type FormEvent } from "react";
import { SITE, track } from "@/constants/site";
import Reveal from "@/components/base/Reveal";

type Status = "idle" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      setErrorMsg("");
      form.reset();
      return;
    }

    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        params.append(key, value);
      }
    });

    track("quote_submit");

    try {
      const res = await fetch(SITE.formUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || "";
      const isOk = res.ok && parsed?.code === "OK";

      if (isOk) {
        setStatus("success");
        setErrorMsg("");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "Não foi possível enviar sua solicitação. Tente novamente.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Erro de conexão. Verifique sua internet e tente novamente.");
    }
  }

  return (
    <section id="contato" className="bg-background-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-accent-600">
              Solicitação de orçamento
            </p>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
              Conte o que você precisa
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              Preencha as informações abaixo e nossa equipe entrará em contato para entender
              sua necessidade e apresentar a solução adequada.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href={`https://wa.me/${SITE.whatsappNumber}`}
                target="_blank"
                rel="noreferrer"
                onClick={() => track("whatsapp_click")}
                className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-[#25D366]"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[#25D366] text-background-50">
                  <i className="ri-whatsapp-line text-xl" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-foreground-900">WhatsApp</span>
                  <span className="block text-sm text-foreground-600">{SITE.whatsappDisplay}</span>
                </span>
              </a>
              <a
                href={SITE.phoneHref}
                onClick={() => track("phone_click")}
                className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-phone-line text-xl" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-foreground-900">Telefone</span>
                  <span className="block text-sm text-foreground-600">{SITE.phoneDisplay}</span>
                </span>
              </a>
              <a
                href={`mailto:${SITE.email}`}
                onClick={() => track("email_click")}
                className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-mail-line text-xl" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-foreground-900">E-mail</span>
                  <span className="block break-all text-sm text-foreground-600">{SITE.email}</span>
                </span>
              </a>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
            >
              {/* honeypot anti-spam */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="contact-form-website-alt"
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="nome" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                    Nome
                  </label>
                  <input
                    id="nome"
                    name="nome"
                    type="text"
                    required
                    placeholder="Seu nome"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="empresa" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                    Empresa
                  </label>
                  <input
                    id="empresa"
                    name="empresa"
                    type="text"
                    placeholder="Nome da empresa"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="whatsapp" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                    WhatsApp
                  </label>
                  <input
                    id="whatsapp"
                    name="whatsapp"
                    type="tel"
                    required
                    placeholder="(00) 00000-0000"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="necessidade" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                    Tipo de necessidade
                  </label>
                  <select
                    id="necessidade"
                    name="necessidade"
                    required
                    defaultValue=""
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500"
                  >
                    <option value="" disabled>
                      Selecione
                    </option>
                    <option value="assistencia">Assistência</option>
                    <option value="motor">Motor</option>
                    <option value="produto">Produto</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="equipamento" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                    Equipamento / modelo
                  </label>
                  <input
                    id="equipamento"
                    name="equipamento"
                    type="text"
                    placeholder="Ex: motor WEG 15cv, bomba, redutor..."
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="descricao" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                    Descrição
                  </label>
                  <textarea
                    id="descricao"
                    name="descricao"
                    rows={4}
                    maxLength={500}
                    required
                    placeholder="Descreva o que está acontecendo com o equipamento..."
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500"
                  />
                </div>
              </div>

              {status === "success" && (
                <p className="mt-4 rounded-md bg-primary-50 px-4 py-3 text-sm font-semibold text-primary-700">
                  Solicitação enviada com sucesso! Nossa equipe entrará em contato em breve.
                </p>
              )}
              {status === "error" && (
                <p className="mt-4 rounded-md bg-accent-50 px-4 py-3 text-sm font-semibold text-accent-700">
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                onClick={() => track("quote_start")}
                className="mt-5 w-full whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-base font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                Enviar solicitação
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}